import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  colors,
  transitionDefault,
  calcEm,
  fontSmoothing,
  fonts,
  breakpoints,
  respondTo,
  calcRem,
} from '~/styles';
import ButtonIcon, { IButtonIcon, IButtonIconPos } from './ButtonIcon';

/** styles */
const basicFontSize = 12;
export const buttonStyling = css``;

const StyledButton = styled.button`
  border: none;
  background: none;
  outline: none;

  font-family: ${fonts.default};
  font-weight: 500;
  text-decoration: none;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  padding: 0 1em;
  display: inline-block;
  align-self: center;
  line-height: 2;
  position: relative;
  cursor: pointer;
  text-decoration: none !important;

  ${transitionDefault};

  &:last-of-type {
    padding-right: 0;
  }
  &:first-of-type {
    padding-left: 0;
  }

  > span {
    display: inline-block;
    position: relative;
    min-width: ${calcEm(83, basicFontSize)};
    text-align: center;
      /* vertical align fix */
    padding: ${calcEm(1, basicFontSize)} ${calcEm(10, basicFontSize)} 0;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;

    ${transitionDefault};
    transition-property: background-color, color, transform;

    ${respondTo(breakpoints.md, css`
      min-width: ${calcEm(50, 12)} !important;
    `)};
  }

  &[disabled] {
    pointer-events: none;
    opacity: 0.3;
  }

  /** sizes */
  &.small {
    font-size: ${calcRem(21.5)};
    text-transform: none;

    > span {
      min-width: 100px;
    }
  }

  &.medium {
    font-size: ${calcRem(20.5)};
    font-weight: 500;
    line-height: 2.6em;
    
    > span {
      min-width: 12.5em;
    }
  }


  &.large {
    font-size: ${calcRem(30)};

    > span {
      padding-left: 3em;
      padding-right: 3em;
    }
  }


  /** color versions */
  &.secondary {
    > span {
      color: ${colors.white};
      background-color: ${colors.black};
      border: ${colors.black} solid 0.1em;
      ${fontSmoothing};
    }
    &:hover:not([disabled]), &.active {
      > span {
        color: ${colors.black};
        background: transparent;
      }
    }
  }

  &.primary {
    > span {
      border: ${colors.black} solid 0.1em;
      color: ${colors.black};
      background: transparent;
    }
    &:hover, &.active {
      > span {
        color: ${colors.white};
        ${fontSmoothing};
        background-color: ${colors.black};
      }
    }
  }

  &.secondary_inverted {
    > span {
      border: ${colors.black} solid 0.1em;
      color: ${colors.black};
      background-color: ${colors.white}
    }
    &:hover, &.active {
      > span {
        background-color: #F8F8F8;
      }
    }
  }

  &.primary_inverted {
    > span {
      border: ${colors.white} solid 0.1em;
      color: ${colors.white};
    }
    &.active {
      > span {
        color: ${colors.black};
        background-color: ${colors.white};
      }
    }
    &:hover:not(.active) {
      > span {
        background-color: rgba(255, 255, 255, 0.15);
      }
    }
  }

  &.text {
    > span {
      border: none;
      padding-left: 0;
      padding-right: 0;
      min-width: 0;
      font-weight: 400;
      ${fontSmoothing};

      &:after {
        content: '';
        position: absolute;
        bottom: 0.45em;
        left: 0;
        right: 0;
        height: 1px;
        background-color: currentColor;
        opacity: 0.5;

        ${transitionDefault};
        transition-timing-function: ease-out;
      }
    }
    &:hover:not([disabled]), &.active {
      > span {
        &:after {
          /* transform: translateY(0.05em); */
          opacity: 1;
        }
      }
    }
  }
`;


interface StylesProps {}
interface Props
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, StylesProps {

  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'primary_inverted' | 'secondary' | 'secondary_inverted' | 'text';
  icon?: IButtonIcon;
  iconPos?: IButtonIconPos;
  active?: boolean;

  as?: React.ElementType;
  href?: string;
  to?: string;
  className?: string;
}

const Button: React.FC<Props> = ({
  children,
  className,
  size = 'small',
  variant = 'primary',
  icon,
  iconPos,
  active = false,
  ...otherProps
}) => {

  return (
    <StyledButton 
      {...otherProps} 
      className={`
        ${size}
        ${variant}
        ${active ? 'active' : ''}
        ${className}
      `}
    >
      <span>
        { icon && iconPos === 'left' && <ButtonIcon {...{icon, iconPos}} /> }
        { children }
        { icon && iconPos === 'right' && <ButtonIcon {...{icon, iconPos}} /> }
      </span>
    </StyledButton>
  );
};

export default Button;
export { Button };



// WEBPACK FOOTER //
// ./src/components/common/Button.tsx